import { apiClient } from './client';

/**
 * 사람 하나 · 열람 기록 (자리 재정의 판 3).
 *
 * 서버: `apps/chat-server/src/modules/hr/people.routes.ts`.
 *
 * ★★ **옛 서버에는 이 라우트가 없다(404).** 부르는 쪽이 실패를 삼키고 «없음» 으로 다뤄야
 *    프론트를 먼저 배포해도 화면이 안 깨진다 — main 은 언제든 그대로 배포할 수 있어야 한다.
 * ★★ **범위 밖 칸은 «키 자체가 없다»** — `null` 이 아니다. 화면이 「못 보는 것」과
 *    「아직 안 채운 것」을 구별할 수 있게 서버가 그렇게 보낸다.
 */

/** 넓은 쪽이 이긴다 — 본인 · 전사 · 그 사람 팀의 팀장 · 그 밖. */
export type PersonScope = 'SELF' | 'ALL' | 'TEAM' | 'PUBLIC';

/**
 * 지금 이 사람에게 적용되는 근무제 (2026-09-02).
 * ★★ **`source` 가 알맹이다.** 개인 배정이 없어도 팀이나 회사 기본으로 이미 무언가 적용되고
 *    있다 — 출처를 안 적으면 화면이 「비었다」로 읽히고 담당자가 개인 배정을 새로 만든다.
 * ★ 판정은 **서버가** 한다(`policyForDay`). 화면이 사다리를 다시 계산하지 않는다.
 */
export interface PersonScheduleDto {
  id: string | null;
  name: string | null;
  type: string | null;
  baseStart: string;
  baseEnd: string;
  /** `USER` 개인 배정 · `TEAM` 팀 배정 · `INSTITUTION` 회사 기본 · `DEFAULT` 코드 09:00 */
  source: 'USER' | 'TEAM' | 'INSTITUTION' | 'DEFAULT';
}

/** 근무제가 «어디서 왔는지» — 화면이 그대로 적는 말. */
export const SCHEDULE_SOURCE_LABEL: Record<string, string> = {
  USER: '개인 배정',
  TEAM: '팀 배정',
  INSTITUTION: '회사 기본',
  DEFAULT: '기본값',
};

export interface PersonCardDto {
  scope: PersonScope;
  /** 범위 밖(PUBLIC)에는 **키가 없다** — 근무 시각도 인사 정보다. */
  schedule?: PersonScheduleDto | null;
  person: {
    userId: string;
    name: string;
    nameEn: string | null;
    email: string;
    position: string | null;
    avatarUrl: string | null;
    status: string;
    employmentStatus: string | null;
    divisionId: string | null;
    divisionName: string | null;
    teamId: string | null;
    teamName: string | null;
    isLeader: boolean;
    /** ↓ 여기서부터는 **범위 밖이면 키가 없다**(옵셔널인 이유). */
    jobLevel?: number | null;
    employeeNumber?: string | null;
    hireDate?: string | null;
    /** 「7년 4개월」 — 서버가 셈한다(2026-09-02). 옛 서버는 안 준다. */
    tenureLabel?: string | null;
    extension?: string | null;
    deskLocation?: string | null;
    employmentType?: string | null;
    phone?: string | null;
    birthday?: string | null;
  };
}

export interface AccessLogRow {
  id: string;
  ts: string;
  eventType: string;
  /** 무엇을 열었나 — 서버가 붙여 준 사람 말. */
  label: string;
  /** 어떤 자격으로 열었나(`ALL`·`TEAM`·`TEAM_LEADER` …). 옛 줄에는 없다. */
  via: string | null;
  actor: { userId: string; name: string; position: string | null; avatarUrl: string | null } | null;
  /** 담당자 화면에서만 — 누가 열렸나. */
  target?: { userId: string; name: string; position: string | null } | null;
}

export interface AccessLogPage {
  rows: AccessLogRow[];
  nextCursor: string | null;
}

export async function fetchPersonCard(userId: string): Promise<PersonCardDto> {
  const { data } = await apiClient.get<PersonCardDto>(`/hr/people/${userId}`);
  return data;
}

/** 내 정보를 본 기록 — 내가 연 줄은 서버가 뺀다. */
export async function fetchMyAccessLog(
  params: { limit?: number; cursor?: string; days?: number } = {},
): Promise<AccessLogPage> {
  const { data } = await apiClient.get<AccessLogPage>('/hr/people/me/access-log', { params });
  return data;
}

/** 개인정보 열람 이력 — **전사를 보는 사람만**(그 밖에는 403). */
export async function fetchAccessLogs(
  params: { limit?: number; cursor?: string; days?: number; actorId?: string; targetId?: string } = {},
): Promise<AccessLogPage> {
  const { data } = await apiClient.get<AccessLogPage>('/hr/people/access-log', { params });
  return data;
}


/**
 * 담당자가 **남의 사진**을 바꾼다 (2026-09-02).
 * ★ 본인 것은 종전대로 `/users/me/avatar` 다 — 길이 둘이 되는 것뿐이다.
 * ★ 규격은 서버가 지킨다: PNG · JPEG · WEBP · 2MB · 매직 바이트. 화면은 그 규격을 미리 적는다.
 */
export async function putPersonAvatar(userId: string, file: Blob): Promise<{ avatarUrl: string }> {
  const { data } = await apiClient.put(`/hr/people/${userId}/avatar`, file, {
    headers: { 'Content-Type': file.type },
  });
  return data;
}

export async function deletePersonAvatar(userId: string): Promise<{ avatarUrl: null }> {
  const { data } = await apiClient.delete(`/hr/people/${userId}/avatar`);
  return data;
}
